import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog.js';
import { Button } from '@/components/ui/button.js';
import { useToast } from '@/components/ui/hooks/use-toast.js';
import { getUsers, updateUserRoles } from '@/lib/api-calls.js';
import { QueryKeys } from '@/lib/queries.js';
import { isCoachRole } from '@airreading/common';
import { UserWithRolesAndProfiles } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState } from 'react';

export interface RevokeCoachProps {
  trigger: ReactNode;
  user: UserWithRolesAndProfiles;
}

export const ManageCoach = ({ trigger, user }: RevokeCoachProps) => {
  const [open, setOpen] = useState(false);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateUserRoles,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();

  const onSubmit = () => {
    return mutateAsync({
      userId: user.id,
      roles: user.userRoles.filter((role) => !isCoachRole(role)).map(({ role, schoolId }) => ({ role, schoolId })),
    })
      .then(() => {
        toast({
          title: 'User teacher role deleted',
        });
        setOpen(false);
        queryClient.invalidateQueries(getUsers());
        queryClient.invalidateQueries({ queryKey: QueryKeys.getUser(user.id) });
      })
      .catch(() => {
        toast({
          title: 'Teacher role delete failed',
        });
      });
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Confirm to delete?</AlertDialogTitle>
          <AlertDialogDescription>
            Do you want to delete <span className="font-bold">{user.fullNameOrEmail}'s</span> teacher role?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              loading={isPending}
              onClick={(e) => {
                e.preventDefault();
                onSubmit();
              }}
            >
              Delete
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
